'use client';

import React, { createContext, useContext } from 'react';

import { DEFAULT_COMMUNITY_SLUG } from '@highlight-cp/constants';
import type { Community } from '@highlight-cp/project-api/types';

const NO_PROVIDER = Symbol('CommunityContext.noProvider');

/**
 * Minimal US Highlight community used when SSR has no community (no subdomain or fetch
 * failure). Matches backend `CommunityService.AllowedTesterCountryCodesDefault`.
 */
export const DEFAULT_COMMUNITY: Community = Object.freeze({
  slug: DEFAULT_COMMUNITY_SLUG,
  allowedTesterCountryCodes: Object.freeze(['US']) as string[],
});

/**
 * Minimal EU Highlight community fallback (GB + US), matching backend `GetDefaultCommunity`
 * when `EnvInfo.IsEu` is true (EPD-1613).
 */
export const DEFAULT_EU_COMMUNITY: Community = Object.freeze({
  slug: DEFAULT_COMMUNITY_SLUG,
  allowedTesterCountryCodes: Object.freeze(['GB', 'US']) as string[],
});

/** Returns the region-appropriate static Highlight community fallback. */
export function getDefaultCommunity(isEuHighlighterHost = false): Community {
  return isEuHighlighterHost ? DEFAULT_EU_COMMUNITY : DEFAULT_COMMUNITY;
}

const CommunityContext = createContext<Community | typeof NO_PROVIDER>(NO_PROVIDER);

export interface UseCommunityContextOptions {
  /**
   * When true, returns null if there is no ancestor `CommunityContextProvider` instead of throwing.
   * Use for hooks or components that may render outside that tree (for example, isolated tests).
   */
  optional?: boolean;
}

interface CommunityContextProviderProps {
  /** Community from host/subdomain fetch; null coalesces to `fallbackCommunity`. */
  community: Community | null;
  /**
   * Used when `community` is null. Defaults to {@link DEFAULT_COMMUNITY} (US).
   * Pass {@link DEFAULT_EU_COMMUNITY} / {@link getDefaultCommunity} on EU highlighter hosts.
   */
  fallbackCommunity?: Community;
  children: React.ReactNode;
}

/**
 * ### CommunityContextProvider
 *
 * @description
 * Provides the current community object to the component tree. When `community` is null
 * (no subdomain or fetch failure), falls back to `fallbackCommunity` or {@link DEFAULT_COMMUNITY}.
 *
 * @example
 * <CommunityContextProvider community={community} fallbackCommunity={getDefaultCommunity(isEu)}>
 *   <App />
 * </CommunityContextProvider>
 *
 * @param props - Component props
 * @param props.community - Community data from host/subdomain, or null
 * @param props.fallbackCommunity - Region-aware static fallback when community is null
 * @param props.children - React node to wrap
 * @returns React component
 */
export const CommunityContextProvider = ({
  community,
  fallbackCommunity = DEFAULT_COMMUNITY,
  children,
}: CommunityContextProviderProps): React.ReactElement => (
  <CommunityContext.Provider value={community ?? fallbackCommunity}>
    {children}
  </CommunityContext.Provider>
);

/**
 * ### useCommunityContext
 *
 * @description
 * Returns the current community from the nearest `CommunityContextProvider`. The provider
 * always supplies a community (falling back to Highlight when SSR had none). By default,
 * this throws outside a provider; pass `{ optional: true }` to safely return null.
 *
 * @example
 * const community = useCommunityContext();
 * if (community.branding) { ... }
 *
 * @example
 * const community = useCommunityContext({ optional: true });
 *
 * @param options - Pass `optional: true` when caller may render outside provider
 * @returns Current community
 * @throws When used outside `CommunityContextProvider` and `optional` is not true
 */
export function useCommunityContext(options: { optional: true }): Community | null;
export function useCommunityContext(options?: { optional?: false }): Community;
export function useCommunityContext(options?: UseCommunityContextOptions): Community | null {
  const context = useContext(CommunityContext);
  if (context === NO_PROVIDER) {
    if (options?.optional) {
      return null;
    }
    throw new Error('useCommunityContext must be used within a CommunityContextProvider');
  }
  return context;
}
